import React, { useState, useEffect } from 'react';
import type { CandidateApplication, CandidateStage } from '../../services/unified/types';
import { useApproveCandidate, useRejectCandidate } from '../../hooks/unified/useCandidatesQueries';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { InterviewSchedulerModal } from './InterviewSchedulerModal';
import { unifiedApiService } from '../../services/unified';

interface CandidateStageManagerProps {
  application: CandidateApplication;
  jobId: string;
  onStageChange?: (newStage: CandidateStage) => void;
}

const STAGES: CandidateStage[] = [
  'Applied',
  'Resume Review',
  'Shortlisted',
  'Interview Scheduled',
  'Interview Completed',
  'Offer Extended',
  'Hired',
];

const STAGE_COLORS: Record<CandidateStage, string> = {
  'Applied': 'bg-blue-500',
  'Resume Review': 'bg-yellow-500',
  'Shortlisted': 'bg-purple-500',
  'Interview Scheduled': 'bg-indigo-500',
  'Interview Completed': 'bg-pink-500',
  'Offer Extended': 'bg-green-500',
  'Hired': 'bg-emerald-600',
  'Rejected': 'bg-red-500',
};

export const CandidateStageManager: React.FC<CandidateStageManagerProps> = ({
  application,
  jobId,
  onStageChange,
}) => {
  const [notes, setNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [existingInterview, setExistingInterview] = useState<any>(null);
  const [isLoadingInterview, setIsLoadingInterview] = useState(false);
  
  const approveMutation = useApproveCandidate();
  const rejectMutation = useRejectCandidate();

  const currentStageIndex = STAGES.indexOf(application.current_stage);
  const isRejected = application.current_stage === 'Rejected';
  const isHired = application.current_stage === 'Hired';
  const isShortlisted = application.current_stage === 'Shortlisted';
  const isInterviewScheduled = application.current_stage === 'Interview Scheduled';
  const canProgress = !isRejected && !isHired && currentStageIndex < STAGES.length - 1;

  // Fetch existing interview for this application
  useEffect(() => {
    const fetchInterview = async () => {
      if (application.id && (isShortlisted || isInterviewScheduled)) {
        setIsLoadingInterview(true);
        try {
          // Try to get interviews for this job and find the one for this application
          const interviews = await unifiedApiService.interviews.getJobInterviews(jobId);
          const interview = interviews.find((i: any) => i.application_id === application.id);
          setExistingInterview(interview || null);
        } catch (error) {
          console.error('Failed to fetch interview:', error);
          setExistingInterview(null);
        } finally {
          setIsLoadingInterview(false);
        }
      }
    };
    fetchInterview();
  }, [application.id, jobId, isShortlisted, isInterviewScheduled]);

  const handleApprove = async () => {
    try {
      const result = await approveMutation.mutateAsync({
        jobId,
        candidateId: application.candidate_id,
        notes: notes || undefined,
      });
      
      if (onStageChange) {
        onStageChange(result.current_stage);
      }
      
      setNotes('');
    } catch (error) {
      console.error('Failed to approve candidate:', error);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      const result = await rejectMutation.mutateAsync({
        jobId,
        candidateId: application.candidate_id,
        reason: rejectReason,
      });
      
      if (onStageChange) {
        onStageChange(result.current_stage);
      }
      
      setRejectReason('');
      setShowRejectModal(false);
    } catch (error) {
      console.error('Failed to reject candidate:', error);
    }
  };

  const handleScheduleInterview = () => {
    setShowInterviewModal(true);
  };

  const handleInterviewScheduled = async () => {
    setShowInterviewModal(false);
    // Refetch interview data
    try {
      const interviews = await unifiedApiService.interviews.getJobInterviews(jobId);
      const interview = interviews.find((i: any) => i.application_id === application.id);
      setExistingInterview(interview || null);
    } catch (error) {
      console.error('Failed to refetch interview:', error);
    }
    // Notify parent to refresh the application data
    if (onStageChange) {
      onStageChange('Interview Scheduled');
    }
  };

  const getStageStatus = (stage: CandidateStage): 'completed' | 'current' | 'upcoming' => {
    const stageIndex = STAGES.indexOf(stage);
    
    if (isRejected) {
      return stageIndex <= currentStageIndex ? 'completed' : 'upcoming';
    }
    
    if (stageIndex < currentStageIndex) return 'completed';
    if (stageIndex === currentStageIndex) return 'current';
    return 'upcoming';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Determine what action button to show based on current stage
  const renderActionButton = () => {
    if (!canProgress) return null;

    // At Shortlisted stage - show Schedule Interview button
    if (isShortlisted) {
      return (
        <button
          onClick={handleScheduleInterview}
          disabled={isLoadingInterview}
          className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {isLoadingInterview ? (
            <>
              <LoadingSpinner size="sm" className="mr-2" />
              Loading...
            </>
          ) : existingInterview ? (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              View/Edit Interview
            </>
          ) : (
            <>
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Schedule Interview
            </>
          )}
        </button>
      );
    }

    // At Interview Scheduled stage - show View Interview and Mark Complete buttons
    if (isInterviewScheduled) {
      return (
        <div className="flex gap-2 flex-1">
          <button
            onClick={handleScheduleInterview}
            disabled={isLoadingInterview}
            className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            View Interview
          </button>
          <button
            onClick={handleApprove}
            disabled={approveMutation.isPending}
            className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
          >
            {approveMutation.isPending ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Processing...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Mark Interview Complete
              </>
            )}
          </button>
        </div>
      );
    }

    // For other stages - show standard Approve button
    return (
      <button
        onClick={handleApprove}
        disabled={approveMutation.isPending}
        className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
      >
        {approveMutation.isPending ? (
          <>
            <LoadingSpinner size="sm" className="mr-2" />
            Processing...
          </>
        ) : (
          <>
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            Approve for Next Stage
          </>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-6">
      {/* Visual Progress Indicator */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Application Progress</h3>
        
        <div className="relative">
          {/* Progress Line */}
          <div className="absolute top-5 left-0 right-0 h-1 bg-gray-200">
            <div
              className={`h-full transition-all duration-500 ${
                isRejected ? 'bg-red-500' : 'bg-blue-500'
              }`}
              style={{
                width: `${((currentStageIndex + 1) / STAGES.length) * 100}%`,
              }}
            />
          </div>

          {/* Stage Indicators */}
          <div className="relative flex justify-between">
            {STAGES.map((stage, index) => {
              const status = getStageStatus(stage);
              const isCurrentStage = stage === application.current_stage;

              return (
                <div key={stage} className="flex flex-col items-center" style={{ width: '12%' }}>
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold transition-all duration-300 ${
                      status === 'completed'
                        ? isRejected && isCurrentStage
                          ? STAGE_COLORS['Rejected']
                          : STAGE_COLORS[stage]
                        : status === 'current'
                        ? `${STAGE_COLORS[stage]} ring-4 ring-blue-200`
                        : 'bg-gray-300'
                    }`}
                  >
                    {status === 'completed' ? (
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span className="text-sm">{index + 1}</span>
                    )}
                  </div>
                  <span
                    className={`mt-2 text-xs text-center ${
                      isCurrentStage ? 'font-semibold text-gray-900' : 'text-gray-600'
                    }`}
                  >
                    {stage}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Interview Info Badge */}
        {existingInterview && (isShortlisted || isInterviewScheduled) && (
          <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-indigo-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span className="text-sm font-medium text-indigo-800">
                  Interview Scheduled: {new Date(existingInterview.scheduled_date).toLocaleString('en-IN', {
                    timeZone: 'Asia/Kolkata',
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  })} IST
                </span>
              </div>
              {existingInterview.google_meet_link && (
                <a
                  href={existingInterview.google_meet_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-indigo-600 hover:text-indigo-800 underline"
                >
                  Join Meet
                </a>
              )}
            </div>
          </div>
        )}

        {/* Rejected Badge */}
        {isRejected && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm font-medium text-red-800">Application Rejected</span>
            </div>
          </div>
        )}

        {/* Hired Badge */}
        {isHired && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="text-sm font-medium text-green-800">Candidate Hired</span>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      {!isRejected && !isHired && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold mb-4">Actions</h3>
          
          {/* Notes Input - hide for shortlisted since interview modal has its own notes */}
          {!isShortlisted && (
            <div className="mb-4">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Notes (Optional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add notes about this stage transition..."
              />
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            {renderActionButton()}

            <button
              onClick={() => setShowRejectModal(true)}
              disabled={rejectMutation.isPending}
              className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
              Reject Candidate
            </button>
          </div>
        </div>
      )}

      {/* Stage History Timeline */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Stage History</h3>
        
        {application.stage_history && application.stage_history.length > 0 ? (
          <div className="space-y-4">
            {[...application.stage_history].reverse().map((entry, index) => (
              <div key={entry.id} className="flex">
                <div className="flex flex-col items-center mr-4">
                  <div
                    className={`w-3 h-3 rounded-full ${
                      entry.stage === 'Rejected'
                        ? 'bg-red-500'
                        : entry.stage === 'Hired'
                        ? 'bg-green-500'
                        : 'bg-blue-500'
                    }`}
                  />
                  {index < application.stage_history.length - 1 && (
                    <div className="w-0.5 h-full bg-gray-300 mt-1" />
                  )}
                </div>
                
                <div className="flex-1 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">{entry.stage}</span>
                    <span className="text-sm text-gray-500">{formatDate(entry.changed_at)}</span>
                  </div>
                  
                  {entry.changed_by_name && (
                    <p className="text-sm text-gray-600 mb-1">
                      Changed by: {entry.changed_by_name}
                    </p>
                  )}
                  
                  {entry.notes && (
                    <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded mt-2">
                      {entry.notes}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-sm">No stage history available</p>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Reject Candidate</h3>
            
            <div className="mb-4">
              <label htmlFor="rejectReason" className="block text-sm font-medium text-gray-700 mb-2">
                Reason for Rejection *
              </label>
              <textarea
                id="rejectReason"
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                placeholder="Please provide a reason for rejecting this candidate..."
                required
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectReason('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={rejectMutation.isPending || !rejectReason.trim()}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {rejectMutation.isPending ? (
                  <>
                    <LoadingSpinner size="sm" className="mr-2" />
                    Rejecting...
                  </>
                ) : (
                  'Confirm Rejection'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interview Scheduler Modal */}
      {showInterviewModal && (
        <InterviewSchedulerModal
          isOpen={showInterviewModal}
          onClose={() => setShowInterviewModal(false)}
          applicationId={application.id}
          onInterviewScheduled={handleInterviewScheduled}
          existingInterview={existingInterview}
        />
      )}
    </div>
  );
};
